import React from "react";
import { Link } from "react-router-dom";

const ArrowIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const About = ({ translations = {} }) => {
  const isSpanish = translations?.header?.home === "Inicio";

  const t = isSpanish
    ? {
        eyebrow: "Sobre San Jose Obrero LLC",
        title: "Estructura, claridad y acompañamiento para negocios latinos.",
        subtitle:
          "Ayudamos a emprendedores y pequeños negocios a ordenar su base empresarial, fiscal y documental antes de tomar decisiones importantes.",
        mainTitle: "Primero entendemos tu situación. Después definimos la ruta.",
        mainText:
          "No trabajamos desde presión ni promesas vacías. Revisamos dónde está parado tu negocio, qué documentos faltan, qué estructura tiene y cuál es el siguiente paso más lógico.",
        cards: [
          {
            title: "Claridad",
            text: "Explicamos el proceso de forma simple para que sepas qué hacer y por qué hacerlo.",
          },
          {
            title: "Orden",
            text: "Organizamos información fiscal, empresarial y documental para reducir errores y confusión.",
          },
          {
            title: "Estrategia",
            text: "Cada recomendación depende de tu situación real, no de un paquete genérico.",
          },
        ],
        missionTitle: "Misión",
        missionText:
          "Guiar a dueños de negocios latinos en la organización de su estructura empresarial, impuestos y preparación financiera.",
        visionTitle: "Visión",
        visionText:
          "Ser una firma de referencia para negocios que buscan operar con más orden, tomar mejores decisiones y prepararse para oportunidades reales.",
        ctaTitle: "Ordena tu siguiente paso",
        ctaText:
          "Solicita una evaluación inicial para revisar qué necesita tu negocio antes de actuar.",
        ctaButton: "Solicitar evaluación",
      }
    : {
        eyebrow: "About San Jose Obrero LLC",
        title: "Structure, clarity, and guidance for Latino-owned businesses.",
        subtitle:
          "We help entrepreneurs and small business owners organize their business, tax, and document foundation before making important decisions.",
        mainTitle: "First we understand your situation. Then we define the route.",
        mainText:
          "We do not work from pressure or empty promises. We review where your business stands, what documents are missing, what structure it has, and what the most logical next step is.",
        cards: [
          {
            title: "Clarity",
            text: "We explain the process in simple terms so you know what to do and why it matters.",
          },
          {
            title: "Order",
            text: "We organize tax, business, and document information to reduce errors and confusion.",
          },
          {
            title: "Strategy",
            text: "Every recommendation depends on your real situation, not on a generic package.",
          },
        ],
        missionTitle: "Mission",
        missionText:
          "To guide Latino business owners in organizing their business structure, taxes, and financial readiness.",
        visionTitle: "Vision",
        visionText:
          "To become a trusted firm for businesses that want more order, better decisions, and realistic preparation for opportunities.",
        ctaTitle: "Organize your next step",
        ctaText:
          "Request an initial evaluation to review what your business needs before taking action.",
        ctaButton: "Request evaluation",
      };

  return (
    <main className="min-h-screen bg-[#050B14] text-fut-text-light overflow-hidden">
      <section className="relative min-h-screen px-6 py-10 md:py-16 flex items-center">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(0,212,255,.26),transparent_27%),radial-gradient(circle_at_84%_28%,rgba(59,130,246,.20),transparent_30%),linear-gradient(135deg,#001A33_0%,#06111f_42%,#020617_100%)]" />
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-fut-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-28 w-[520px] h-[520px] rounded-full bg-blue-500/10 blur-3xl" />

        <div className="relative container mx-auto max-w-screen-xl w-full pt-24 md:pt-32">
          <div className="mb-8 max-w-4xl">
            <p className="text-fut-primary font-bold uppercase tracking-[.22em] mb-4">
              {t.eyebrow}
            </p>
            <h1 className="text-4xl md:text-6xl font-display font-black leading-tight mb-5 text-white">
              {t.title}
            </h1>
            <p className="text-lg md:text-xl text-fut-text-light/90 leading-relaxed max-w-3xl">
              {t.subtitle}
            </p>
          </div>

          <div className="relative rounded-[3rem] bg-white/[.055] shadow-2xl shadow-fut-primary/10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#001A33]/95 via-[#06111f]/90 to-black/95" />
            <div className="absolute left-0 top-0 h-full w-1 bg-fut-primary" />

            <div className="relative z-10 p-5 md:p-8 lg:p-10 space-y-6">
              <div className="grid lg:grid-cols-[.95fr_1.05fr] gap-6 items-stretch">
                <div className="relative min-h-[360px] rounded-[2.35rem] overflow-hidden shadow-xl shadow-fut-primary/8">
                  <img
                    src="/images/businessman-2.webp"
                    alt="San Jose Obrero LLC"
                    className="absolute inset-0 w-full h-full object-cover object-center opacity-95"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 rounded-2xl bg-black/28 backdrop-blur-md p-5">
                    <p className="text-fut-primary font-black uppercase tracking-[.18em] text-xs">
                      San Jose Obrero LLC
                    </p>
                    <p className="text-white text-2xl font-black mt-2">
                      Business structure · Taxes · Documentation
                    </p>
                  </div>
                </div>

                <div className="rounded-[2.35rem] bg-black/22 backdrop-blur-xl p-6 md:p-8 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-3 mb-5">
                    <span className="rounded-full bg-fut-primary text-fut-bg-dark px-4 py-2 text-xs font-black uppercase tracking-[.18em]">
                      San Jose Obrero LLC
                    </span>
                    <span className="rounded-full text-fut-primary px-4 py-2 text-xs font-bold uppercase tracking-[.18em]">
                      {t.eyebrow}
                    </span>
                  </div>

                  <h2 className="text-3xl md:text-5xl font-display font-black leading-tight mb-4 text-white">
                    {t.mainTitle}
                  </h2>

                  <p className="text-fut-text-light/90 text-lg leading-relaxed">
                    {t.mainText}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                {t.cards.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-[2.35rem] bg-white/[.05] backdrop-blur-xl p-6"
                  >
                    <p className="text-fut-primary font-black text-xl mb-3">
                      {item.title}
                    </p>
                    <p className="text-fut-text-light/80 leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>

              <div className="grid lg:grid-cols-2 gap-4">
                <div className="rounded-[2.35rem] bg-black/18 p-6">
                  <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-3">
                    {t.missionTitle}
                  </p>
                  <p className="text-2xl font-display font-black text-white leading-tight">
                    {t.missionText}
                  </p>
                </div>

                <div className="rounded-[2.35rem] bg-black/18 p-6">
                  <p className="text-fut-primary font-black uppercase tracking-[.18em] text-sm mb-3">
                    {t.visionTitle}
                  </p>
                  <p className="text-xl font-semibold text-fut-text-light/90 leading-relaxed">
                    {t.visionText}
                  </p>
                </div>
              </div>

              <div className="rounded-[2.35rem] bg-fut-primary text-fut-bg-dark p-6 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-5">
                <div>
                  <p className="text-2xl font-black">{t.ctaTitle}</p>
                  <p className="font-semibold opacity-90 mt-1">{t.ctaText}</p>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-fut-bg-dark text-white px-5 py-3 font-black shrink-0"
                >
                  {t.ctaButton}
                  <ArrowIcon />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
